import React, { useState, useEffect } from 'react'
import SideBar from '../components/SideBar'
import DetailsHeaderBar from '../components/DetailsHeaderBar'
import DetailSection from '../components/DetailSection'
import { useNavigate, useParams } from 'react-router-dom'
import { MdOutlineImage } from "react-icons/md";
import { TbCategory, TbCategory2 } from 'react-icons/tb'
import { categoryService } from '../services/categoryService';

function CategoryDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        if (id) {
            fetchCategoryDetails();
        }
    }, [id]);

    const fetchCategoryDetails = async () => {
        try {
            setLoading(true);
            const response = await categoryService.getCategory(id);
            
            if (response && response.category) {
                // Format the category data to match your structure
                const formattedCategory = {
                    id: response.category.id,
                    name: response.category.name,
                    slug: response.category.slug,
                    description: response.category.description,
                    thumbnail: response.category.image_url || "https://i.pinimg.com/originals/ec/99/db/ec99dbf221d03cf3c93ea8c0336501a1.jpg",
                    images: response.category.image_url ? [response.category.image_url] : [],
                    status: response.category.status,
                    created_by: response.category.created_by,
                    created_at: response.category.created_at,
                    updated_at: response.category.updated_at,
                    subcategories: response.category.subcategories || [],
                    meta_title: response.category.meta_title,
                    meta_description: response.category.meta_description,
                    meta_keywords: response.category.meta_keywords || [],
                    created_by_user: response.category.created_by_user,
                    image_url: response.category.image_url
                };
                
                setCategory(formattedCategory);
            } else {
                setCategory(null);
            }
        } catch (error) {
            console.error('Failed to fetch category details:', error);
            setCategory(null);
        } finally {
            setLoading(false);
        }
    };

    const goToEdit = () => {
        navigate(`/category-edit/${id}`);
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await categoryService.deleteCategory(id);
                alert('Category deleted successfully');
                navigate('/manage-category');
            } catch (error) {
                alert(error.response?.data?.message || 'Failed to delete category');
            }
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await categoryService.updateCategoryStatus(id, newStatus);
            alert(`Category ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            fetchCategoryDetails(); // Refresh data
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to update status');
        }
    };

    const getProductImages = () => {
        if (!category || !category.image_url) return [];
        return [category.image_url];
    };

    if (loading) {
        return (
            <div className="manage-member-layout">
                <SideBar />
                <div className="product-details-main">
                    <DetailsHeaderBar
                        title="Category Details"
                        subtitle="You can edit the category details to update the category details"
                        breadcrumbs={[
                            {label: "Home", path: "/manage-member"},
                            {label: "Manage Category", path: "/manage-category"},
                            {label: "Category Details" }
                        ]}
                    />
                    <div style={{textAlign: 'center', padding: '50px'}}>Loading...</div>
                </div>
            </div>
        );
    }

    if (!category) {
        return (
            <div className="manage-member-layout">
                <SideBar />
                <div className="product-details-main">
                    <DetailsHeaderBar
                        title="Category Details"
                        subtitle="You can edit the category details to update the category details"
                        breadcrumbs={[
                            {label: "Home", path: "/manage-member"},
                            {label: "Manage Category", path: "/manage-category"},
                            {label: "Category Details" }
                        ]}
                    />
                    <div style={{textAlign: 'center', padding: '50px', color: '#d9534f'}}>
                        Category not found. Please check if the category ID is correct.
                    </div>
                    <div style={{textAlign: 'center'}}>
                        <button 
                            onClick={() => navigate('/manage-category')}
                            style={{
                                padding: '10px 20px',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Back to Categories
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-member-layout">
            <SideBar />
            <div className="product-details-main">
                <DetailsHeaderBar
                    title="Category Details"
                    subtitle="You can edit the category details to update the category details"
                    breadcrumbs={[
                        {label: "Home", path: "/manage-member"},
                        {label: "Manage Category", path: "/manage-category"},
                        {label: "Category Details" }
                    ]}
                />

                <DetailSection
                    title="Category Image"
                    icon={MdOutlineImage}
                    showEdit={true}
                    onEdit={goToEdit}
                    fields={[
                        {
                            label: "Images",
                            value: getProductImages().length > 0 ? (
                                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                                {getProductImages().map((src, index) => (
                                    <img
                                    key={index}
                                    src={src}
                                    alt={`Category ${index}`}
                                    onClick={() => setPreviewImage(src)}
                                    style={{
                                        height: 90,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                        border: "1px solid #ddd",
                                        cursor: "pointer",
                                    }}
                                    />
                                ))}
                                </div>
                            ) : (
                                "No Images"
                            ),
                        }
                    ]}
                />

                <DetailSection
                    title="Basic Details"
                    icon={TbCategory}
                    onEdit={goToEdit}
                    fields={[
                        { label: "Category name", value: category.name },
                        { label: "Category Slug", value: category.slug },
                        { label: "Status", value: category.status },
                        { label: "Description", value: category.description || "No description" },
                        { label: "Created By", value: category.created_by_user?.name || "Unknown" },
                        { label: "Created At", value: new Date(category.created_at).toLocaleDateString() },
                    ]}
                />
                
                {category.subcategories && category.subcategories.length > 0 && (
                    <DetailSection
                        title="Sub Categories"
                        icon={TbCategory2}
                        showEdit={false}
                        fields={[
                            {
                            label: "Sub Categories",
                            value: category.subcategories.length
                                ? category.subcategories.map(sub => sub.name).join(", ")
                                : "No Sub Categories",
                            },
                        ]}
                    />
                )}

                {(category.meta_title || category.meta_description || (category.meta_keywords && category.meta_keywords.length > 0)) && (
                    <DetailSection
                        title="SEO Information"
                        icon={TbCategory}
                        showEdit={false}
                        fields={[
                            { label: "Meta Title", value: category.meta_title || "Not set" },
                            { label: "Meta Description", value: category.meta_description || "Not set" },
                            { 
                                label: "Meta Keywords", 
                                value: category.meta_keywords && category.meta_keywords.length > 0
                                    ? Array.isArray(category.meta_keywords) 
                                        ? category.meta_keywords.join(", ")
                                        : String(category.meta_keywords)
                                    : "Not set"
                            },
                        ]}
                    />
                )}

                {/* <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button 
                        onClick={() => handleStatusChange(category.status === 'active' ? 'inactive' : 'active')}
                        style={{
                            padding: '8px 16px',
                            background: category.status === 'active' ? '#f0ad4e' : '#5cb85c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        {category.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                        onClick={handleDelete}
                        style={{
                            padding: '8px 16px',
                            background: '#d9534f',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Delete
                    </button>
                </div> */}

                {previewImage && (
                    <div
                        onClick={() => setPreviewImage(null)}
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100vw",
                            height: "100vh",
                            background: "rgba(0,0,0,0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 9999,
                        }}
                    >
                        <div style={{ position: "relative" }}>
                            <img
                                src={previewImage}
                                alt="Preview"
                                style={{
                                    maxWidth: "90vw",
                                    maxHeight: "90vh",
                                    borderRadius: 10,
                                }}
                            />

                            <button
                                onClick={() => setPreviewImage(null)}
                                style={{
                                    position: "absolute",
                                    top: -10,
                                    right: -10,
                                    background: "#e53935",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: 30,
                                    height: 30,
                                    cursor: "pointer",
                                    fontSize: 16,
                                    fontWeight: "bold",
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CategoryDetails